import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, RefreshCw } from 'lucide-react';
import { Button } from '@tadagram/ui';
import { SeedingAccount } from '../types';
import { authService } from '../services/auth';

interface TikTokQRModalProps {
  account: SeedingAccount;
  /** Initial QR data pushed via SSE — can be refreshed by polling */
  initialQR?: { qr_base64: string; expires_at: number };
  onClose: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '/api/v1';
const POLL_INTERVAL_MS = 5_000; // re-poll every 5 s

export const TikTokQRModal: React.FC<TikTokQRModalProps> = ({
  account,
  initialQR,
  onClose,
}) => {
  const { t } = useTranslation('account');

  const [qrBase64, setQrBase64] = useState<string | null>(initialQR?.qr_base64 ?? null);
  const [expiresAt, setExpiresAt] = useState<number | null>(initialQR?.expires_at ?? null);
  const [countdown, setCountdown] = useState<number>(0);
  const [loading, setLoading] = useState(!initialQR);
  const [error, setError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Fetch QR from backend ─────────────────────────────────────────────────
  const fetchQR = useCallback(async () => {
    try {
      const token = authService.getSessionToken();
      const res = await fetch(`${API_BASE_URL}/accounts/${account.id}/tiktok-qr`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.ok) {
        const data = await res.json();
        setQrBase64(data.qr_base64);
        setExpiresAt(data.expires_at);
        setError(null);
      } else if (res.status === 404) {
        // QR not generated yet — keep waiting
        setError(null);
      } else if (res.status === 410) {
        // Expired — clear display, wait for next MQTT push
        setQrBase64(null);
        setExpiresAt(null);
      } else {
        setError(`HTTP ${res.status}`);
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  // ── Polling ───────────────────────────────────────────────────────────────
  useEffect(() => {
    // Initial fetch if no SSE data was provided
    if (!initialQR) {
      fetchQR();
    }

    pollRef.current = setInterval(fetchQR, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchQR, initialQR]);

  // ── Countdown timer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (!expiresAt) return;

    const tick = () => {
      const remaining = Math.max(0, expiresAt - Math.floor(Date.now() / 1000));
      setCountdown(remaining);
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [expiresAt]);

  // ── Escape key ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-tg-bg rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-tg-border">
          <div>
            <h2 className="font-semibold text-tg-text text-base">TikTok QR Login</h2>
            <p className="text-xs text-tg-hint mt-0.5">
              {account.username || account.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-tg-hint hover:bg-tg-bg-secondary transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-6 flex flex-col items-center gap-4">
          {loading && (
            <div className="flex flex-col items-center gap-3 py-8">
              <RefreshCw size={32} className="text-tg-primary animate-spin" />
              <p className="text-sm text-tg-hint">Waiting for QR code...</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-sm text-tg-error text-center py-4">{error}</div>
          )}

          {!loading && !error && !qrBase64 && (
            <div className="text-sm text-tg-hint text-center py-4">
              QR code not yet available — the worker is generating it...
            </div>
          )}

          {qrBase64 && (
            <>
              <div className="p-3 bg-white rounded-xl shadow-inner">
                <img
                  src={`data:image/png;base64,${qrBase64}`}
                  alt="TikTok Login QR Code"
                  className="w-56 h-56 block"
                />
              </div>

              {/* Countdown */}
              {expiresAt && countdown > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-tg-hint">
                  <RefreshCw size={12} className={countdown < 10 ? 'text-tg-error animate-spin' : 'text-tg-hint'} />
                  <span>
                    {countdown < 10
                      ? `Refreshing in ${countdown}s...`
                      : `Expires in ${countdown}s`}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Instructions */}
          <ol className="text-sm text-tg-hint space-y-1 list-decimal list-inside text-left w-full">
            <li>Open TikTok on your phone</li>
            <li>Tap <strong>Profile</strong> → menu → <strong>Use QR Code</strong></li>
            <li>Scan the code above</li>
            <li>Confirm login on your phone</li>
          </ol>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
        </div>
      </div>
    </div>
  );
};
